import { useMemo } from "react";

import {
  IS_BOX_PLOT_CHART,
  IS_CROSS_PLOT_CHART,
  IS_MOSAIC_CHART,
  IS_STACKED_BAR_CHART,
  IS_STACKED_BAR_HORIZONTAL_CHART,
  IS_TREND_DATE_CHART
} from "constants/chart.constants";

import { EvaChart } from "../../../constants/charts.enums";
import { ChartAxisType } from "../../../models/chart";

/** Which axes show a field picker for this chart type. */
export const getAxisFieldSelection = (
  chartType: EvaChart,
  showScatter?: boolean
): Record<ChartAxisType, boolean> => ({
  x:
    IS_CROSS_PLOT_CHART(chartType) ||
    (IS_BOX_PLOT_CHART(chartType) && !!showScatter) ||
    IS_MOSAIC_CHART(chartType) ||
    chartType === EvaChart.Probit ||
    IS_STACKED_BAR_CHART(chartType) ||
    IS_STACKED_BAR_HORIZONTAL_CHART(chartType) ||
    IS_TREND_DATE_CHART(chartType),
  y:
    IS_TREND_DATE_CHART(chartType) ||
    IS_CROSS_PLOT_CHART(chartType) ||
    IS_STACKED_BAR_CHART(chartType) ||
    IS_STACKED_BAR_HORIZONTAL_CHART(chartType) ||
    IS_BOX_PLOT_CHART(chartType)
});

/**
 * Custom hook to determine if field selection is allowed for a specific chart axis based on chart type.
 *
 * This hook evaluates whether field selection should be enabled for X and Y axes
 * depending on the provided chart type. The answer is derived in the same render as the
 * inputs, so switching modes hides or shows the controls on that paint rather than the next.
 *
 * @param {ChartAxisType} axisType - The axis type to check ('x' or 'y')
 * @param {EvaChart} chartType - The type of chart being rendered
 * @param showHistogram
 * @param showScatter
 * @returns {boolean} Whether field selection is allowed for the specified axis
 */
const useAxisFieldSelection = (
  axisType: ChartAxisType,
  chartType: EvaChart,
  showHistogram?: boolean,
  showScatter?: boolean
): boolean => {
  const allowFieldSelection = useMemo(
    () => getAxisFieldSelection(chartType, showScatter),
    [chartType, showScatter]
  );

  return allowFieldSelection[axisType];
};

export default useAxisFieldSelection;
